import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  ArrowDown,
  ArrowUp,
  Bookmark,
  CornerDownLeft,
  Flag,
  X,
} from "lucide-react";
import { adjacentCue, findScriptMatches } from "../core/navigation";
import type { ControlCommand, ParsedScript } from "../types";

export const ScriptNavigator = memo(function ScriptNavigator({
  parsed,
  wordIndex,
  command,
  onClose,
  open,
}: {
  parsed: ParsedScript;
  wordIndex: number;
  command: (command: ControlCommand) => void;
  onClose: () => void;
  open: boolean;
}) {
  const [query, setQuery] = useState("");
  const [selection, setSelection] = useState(-1);
  const [pickup, setPickup] = useState<number | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const matches = useMemo(
    () => findScriptMatches(parsed.words, query),
    [parsed, query],
  );
  const previousCue = adjacentCue(parsed.markers, wordIndex, -1);
  const nextCue = adjacentCue(parsed.markers, wordIndex, 1);
  useEffect(() => {
    if (open) input.current?.focus();
  }, [open]);
  useEffect(() => {
    setSelection(-1);
    setPickup(null);
  }, [parsed]);
  const seek = (index: number) => command({ type: "seek", wordIndex: index });
  const navigate = (direction: -1 | 1) => {
    if (!matches.length) return;
    let next: number;
    if (selection < 0) {
      next = matches.findIndex((index) => index >= wordIndex);
      if (direction === -1) {
        next = -1;
        for (let i = matches.length - 1; i >= 0; i--)
          if (matches[i] <= wordIndex) {
            next = i;
            break;
          }
      }
      if (next < 0) next = direction === 1 ? 0 : matches.length - 1;
    } else next = (selection + direction + matches.length) % matches.length;
    setSelection(next);
    seek(matches[next]);
  };
  const selectedWord = selection >= 0 ? matches[selection] : undefined;
  const excerpt =
    selectedWord === undefined
      ? ""
      : parsed.words
          .slice(Math.max(0, selectedWord - 3), selectedWord + 12)
          .map((word) => word.text)
          .join(" ");
  return (
    <section
      className="script-navigator"
      aria-label="Find and cue script"
      hidden={!open}
    >
      <div className="script-find-row">
        <input
          ref={input}
          aria-label="Find phrase in script"
          placeholder="Find a word or phrase…"
          value={query}
          maxLength={300}
          onChange={(event) => {
            setQuery(event.target.value);
            setSelection(-1);
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              navigate(event.shiftKey ? -1 : 1);
            }
            if (event.key === "Escape") {
              event.preventDefault();
              onClose();
            }
          }}
        />
        <button data-help={matches.length ? "Move every display to the previous matching phrase. In the search field, press Shift+Enter." : "Enter a matching word or phrase before moving to a previous result."}
          className="icon-button"
          aria-label="Previous match"
          title="Previous match · Shift+Enter"
          disabled={!matches.length}
          onClick={() => navigate(-1)}
        >
          <ArrowUp size={16} />
        </button>
        <button data-help={matches.length ? "Move every display to the next matching phrase. In the search field, press Enter." : "Enter a matching word or phrase before moving to a result."}
          className="icon-button"
          aria-label="Next match"
          title="Next match · Enter"
          disabled={!matches.length}
          onClick={() => navigate(1)}
        >
          <ArrowDown size={16} />
        </button>
        <button data-help="Close the finder while keeping the current reading position."
          className="icon-button"
          aria-label="Close script finder"
          onClick={onClose}
        >
          <X size={16} />
        </button>
      </div>
      <p className="script-find-result" role="status" data-help="Shows the selected search result, total matches and its script word position. Use the arrows or Enter to cue a match on every display." tabIndex={0}>
        {!query.trim()
          ? "Find a phrase, then press Enter to cue it on every display."
          : !matches.length
            ? "No matching phrase. Try fewer words."
            : `${selection >= 0 ? selection + 1 : "—"} of ${matches.length} ${matches.length === 1 ? "match" : "matches"}${selectedWord === undefined ? " · Enter to cue" : ` · Word ${selectedWord + 1}`}`}
      </p>
      {excerpt && <p className="script-find-excerpt">{excerpt}</p>}
      <div className="script-navigation-actions">
        <button data-help={previousCue === undefined ? "There is no earlier cue marker in this script." : "Move every display to the previous cue marker."}
          className="subtle-button"
          disabled={previousCue === undefined}
          onClick={() => previousCue !== undefined && seek(previousCue)}
        >
          <Flag size={13} />
          Previous cue
        </button>
        <button data-help={nextCue === undefined ? "There is no later cue marker in this script." : "Move every display to the next cue marker."}
          className="subtle-button"
          disabled={nextCue === undefined}
          onClick={() => nextCue !== undefined && seek(nextCue)}
        >
          <Flag size={13} />
          Next cue
        </button>
        <button data-help="Remember the current reading position as a pickup point until the script changes."
          className="subtle-button"
          onClick={() => setPickup(wordIndex)}
          title="Remember this position until the script changes"
        >
          <Bookmark size={13} />
          Set pickup
        </button>
        <button data-help={pickup === null ? "Set a pickup point first, then return to it here." : "Move every display back to the saved pickup point."}
          className="subtle-button"
          disabled={pickup === null}
          onClick={() => pickup !== null && seek(pickup)}
        >
          <CornerDownLeft size={13} />
          {pickup === null ? "Return to pickup" : `Pickup · word ${pickup + 1}`}
        </button>
      </div>
    </section>
  );
});
